import {
  DossDataFormat,
  TableCellData,
  UserEnteredLogicInputs,
  getWebPath,
  isOverwritableLookupColumnType,
  isTableCellData,
} from '@doss/common-all'
import { Autocomplete, inputBaseClasses, inputClasses, outlinedInputClasses } from '@mui/material'
import { CheckedState } from '@radix-ui/react-checkbox'
import { compact, first, get, isNil, nth, uniqBy } from 'lodash'
import { Loader2 } from 'lucide-react'
import {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FaFilter } from 'react-icons/fa'
import { Else, If, Then } from 'react-if'
import { tss } from 'tss-react/mui'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { NoAccess, NoAccessObjectType } from '~/@/components/no-access'
import Typography from '~/@/components/ui/typography'
import { cn } from '~/@/lib/utils'
import {
  BooleanOperator,
  BreadcrumbObjectType,
  DataType,
  Table,
  TableColumn,
  TableFilterInput,
  TableSortInput,
  useGetDetailedTableQuery,
  useGetLookupTableRowsLazyQuery,
  useGetSelectedLookupRowsLazyQuery,
} from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { AdaptiveLookupScroll } from '~/common/utils/adaptive-scroll'
import { localKeyMap } from '~/common/utils/keymap'
import { ConditionExpression } from '~/components/logic-expression/ConditionExpression'
import type { LookupFilterContextType } from '~/components/logic-expression/utils'
import Link from '~/components/shared/Link'
import {
  RecordCellEditOrigin,
  useEditableTableCellOriginContext,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import { LookupMenuItem } from '~/components/ui/molecules/lookup-menu-item/LookupMenuItem'
import { LookupMenuItemPreview } from '~/components/ui/molecules/lookup-menu-item/LookupMenuItemPreview'
import { TextField } from '~/components/ui/molecules/text-field/TextField'
import { LookupCellChipV2 } from '../../ui/molecules/lookup-cell-chip/LookupCellChipV2'
import { LookupTableRow } from '../types'
import { useLookupContext } from './hooks/LookupProvider'
import { LOOKUP_LIMIT, LookupState, LookupValue } from './LookupCell'
import { LookupDialogHeader } from './LookupDialogHeader'
import {
  type LookupFilterFieldMaps,
  getLookupFilterFieldNameMapTablePart,
  mergeLookupFilterFieldMaps,
} from './lookupFilterFieldMaps'
import { LookupRowOptionsListSkeleton } from './LookupRowOptionsListSkeleton'
import { OverwriteLookupBanner } from './OverwriteLookupBanner'
import { OverwriteLookupButton } from './OverwriteLookupButton'

export interface LookupRowOptionsListProps {
  autoFocus?: boolean
  autoSave?: boolean
  onClose?: () => void
  onSave?: () => void
  column: Pick<TableColumn, 'id' | 'name' | 'type'>
  lookupTable?: Pick<Table, 'id' | 'name'> & { parentTable?: { id: string } | null }
  isArray: boolean
  values?: LookupValue | null
  parentRowId?: string
  userEnteredValues: UserEnteredLogicInputs
  lookupFilterExpressionId?: string
  /** When present, shown directly in the filter condition UI without fetching expression JSON. */
  lookupFilterExpressionString?: string | null
  /** Map of field/column IDs to display names for formatting the filter expression string. */
  lookupFilterFieldNameMap?: Record<string, string> | null
  /** Map of field/column IDs to context tooltip text (e.g. "Table: Orders", "Parent table: Companies"). */
  lookupFilterFieldContextNameMap?: Record<string, string> | null
  /** Map of field/column IDs to context type for filter expression color coding. */
  lookupFilterFieldContextTypeMap?: Record<string, LookupFilterContextType> | null
  /** When set and filter maps are not provided, fetch detailed table here (lazy: only when this list is shown). */
  lookupTableId?: string
  /** When set with lookupTableId, merged with table maps for filter display (e.g. form-v2 BulkSelectLookupModal). */
  formLookupFilterMaps?: LookupFilterFieldMaps
  isUnlinkedLookup: boolean
  lookupState: LookupState
  setLookupState: (newState: LookupState) => void
  lookupColumnGroupLookupRowIds?: string[]
  categoryHasHiddenFieldsWithLookupColumn?: boolean
  loadingLookupGroup?: boolean
  showSelectAllButton?: boolean
  error?: string
  /** When true, skip the inner text error because an outer wrapper displays it. */
  hideInnerError?: boolean
  hideUnlinkLookupButton?: boolean
  /** Merged onto the root container (e.g. full width in dialogs) */
  className?: string
  /** When true, "add all" merges visible rows into the current selection (bulk lookup modals). Default replaces selection. */
  selectAllAddsToSelection?: boolean
  /** When true, hides the inner table name + close row (use when the parent dialog already provides a title and close control). */
  hideLookupDialogHeader?: boolean
  /** Optional filters for this lookup request only; ANDed with search + lookup expression. */
  lookupFilters?: TableFilterInput[] | null
  lookupSorts?: TableSortInput[] | null
  lookupFilterBooleanOperator?: BooleanOperator | null
  /**
   * When true, the results/preview pane grows to fill the available height instead of the fixed
   * desktop height, so a resizable container (e.g. the draggable lookup popover) can show more rows.
   * Requires the root to be given a flexing height via `className` (e.g. `flex-1 min-h-0`).
   */
  fillHeight?: boolean
}

/** Two-pane lookup picker: paginated row list + preview of the highlighted row. */
export function LookupRowOptionsList({
  onClose,
  onSave,
  column,
  lookupTable,
  isArray,
  values,
  parentRowId,
  userEnteredValues,
  lookupFilterExpressionId,
  lookupFilterExpressionString,
  lookupFilterFieldNameMap: lookupFilterFieldNameMapProp,
  lookupFilterFieldContextNameMap: lookupFilterFieldContextNameMapProp,
  lookupFilterFieldContextTypeMap: lookupFilterFieldContextTypeMapProp,
  lookupTableId,
  formLookupFilterMaps,
  autoFocus,
  isUnlinkedLookup,
  lookupState,
  setLookupState,
  lookupColumnGroupLookupRowIds,
  categoryHasHiddenFieldsWithLookupColumn = false,
  loadingLookupGroup = false,
  showSelectAllButton = false,
  selectAllAddsToSelection = false,
  hideUnlinkLookupButton = false,
  error,
  hideInnerError = false,
  className,
  hideLookupDialogHeader = false,
  lookupFilters,
  lookupSorts,
  lookupFilterBooleanOperator,
  fillHeight = false,
}: LookupRowOptionsListProps) {
  const { isMobile } = useDeviceType()
  const { t } = useTypedTranslation()
  const { classes } = useStyles()
  const origin = useEditableTableCellOriginContext()
  const isFormContext = origin === RecordCellEditOrigin.FORM || origin === null
  const { isAdmin, isInternal } = useGlobalContext()
  // The filter expression display is admin/internal-only: it confuses non-technical users, but
  // records are still filtered by the expression for everyone.
  const canViewLookupFilterExpression = isAdmin || isInternal
  // Lazy fetch when we have lookupTableId but no maps. Only runs when this list is mounted and
  // maps are not provided (on Bulk Select Lookup Modals and LookupCell in Table Details)
  const needMapsFromQuery = Boolean(lookupTableId && !lookupFilterFieldNameMapProp)
  const { data: detailedTableData } = useGetDetailedTableQuery({
    variables: { tableId: lookupTableId ?? '' },
    skip: !needMapsFromQuery || !lookupTableId,
  })
  const table = detailedTableData?.table
  const queriedFilterMaps = useMemo(() => {
    if (!table) {
      return undefined
    }
    const tableMaps = getLookupFilterFieldNameMapTablePart(table, t)
    return formLookupFilterMaps
      ? mergeLookupFilterFieldMaps(formLookupFilterMaps, tableMaps)
      : tableMaps
  }, [table, formLookupFilterMaps, t])
  const lookupFilterFieldNameMap = lookupFilterFieldNameMapProp ?? queriedFilterMaps?.fieldNameMap
  const lookupFilterFieldContextNameMap =
    lookupFilterFieldContextNameMapProp ?? queriedFilterMaps?.fieldContextNameMap
  const lookupFilterFieldContextTypeMap =
    lookupFilterFieldContextTypeMapProp ?? queriedFilterMaps?.fieldContextTypeMap
  const autocompleteRef = useRef<HTMLInputElement>(null)
  const optionRefs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({})
  const [searchQuery, setSearchQuery] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState<string>(
    !Array.isArray(values) ? (values?.data?.toString() ?? '') : '',
  )
  const [isOverwritingLookup, setIsOverwritingLookup] = useState(isUnlinkedLookup)
  const [highlightedOption, setHighlightedOption] = useState<LookupTableRow | null>(null)
  const [lastSelectedOptions, setLastSelectedOptions] = useState<LookupTableRow[] | null>(
    Array.isArray(lookupState) ? lookupState : null,
  )
  const pageAbortController = useRef(new AbortController())
  const isInitialRender = useRef(true)
  // Lazy query starts loading=false so the empty state doesn't flash before results arrive.
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
  const [loadSearch, { loading }] = useGetLookupTableRowsLazyQuery({
    fetchPolicy: 'cache-and-network',
  })
  const isLookupOnChildTable = !isNil(lookupTable?.parentTable)

  const [options, setOptions] = useState<LookupTableRow[]>([])
  const hasScheduledInitialFetch = useRef(false)
  const [getSelectedLookupRows] = useGetSelectedLookupRowsLazyQuery()

  const [shouldFetchMore, setShouldFetchMore] = useState(true)
  const { getCellDisplay, isUnauth } = useLookupContext()
  const infiniteScroll = useRef<HTMLDivElement>(null)

  // Initialize the adaptive lookup scroll
  const lookupScroll = useRef(new AdaptiveLookupScroll(LOOKUP_LIMIT))
  const lookupStateRef = useRef(lookupState)

  // Keep async search completion checks aligned with the latest selection.
  useEffect(() => {
    lookupStateRef.current = lookupState
  }, [lookupState])

  /** Stable string so we only reset lookup list state when filters/sorts/boolean op change (not on every parent re-render). */
  const lookupRefinementKey = useMemo(
    () =>
      JSON.stringify({
        filters: lookupFilters ?? [],
        sorts: lookupSorts ?? [],
        booleanOperator: lookupFilterBooleanOperator ?? null,
      }),
    [lookupFilters, lookupSorts, lookupFilterBooleanOperator],
  )

  const buildLookupRowsQueryInput = useCallback(
    (params: { searchString: string | null; pagination: { limit: number; offset?: number } }) => ({
      tableId: lookupTable?.id ?? '',
      queriedFromRowId: parentRowId,
      searchString: params.searchString ?? '',
      pagination: params.pagination,
      lookupFilterExpressionId,
      userEnteredData: userEnteredValues,
      ...(lookupFilters != null && lookupFilters.length > 0
        ? {
            filters: lookupFilters,
            filterBooleanOperator: lookupFilterBooleanOperator ?? undefined,
          }
        : {}),
      ...(lookupSorts != null && lookupSorts.length > 0 ? { sorts: lookupSorts } : {}),
    }),
    [
      lookupTable?.id,
      parentRowId,
      lookupFilterExpressionId,
      userEnteredValues,
      lookupFilters,
      lookupSorts,
      lookupFilterBooleanOperator,
    ],
  )

  /**
   * Single fetch entry point: page 1 whenever table, refinement (filters/sorts/boolean), or
   * search query changes. Fetch is debounced internally; abort + scroll reset happen immediately
   * on each change. Row hydration runs in a separate effect so we never abort each other.
   */
  useEffect(() => {
    if (!lookupTable) {
      return
    }

    // Abort + reset immediately on every change (not debounced)
    pageAbortController.current.abort()
    pageAbortController.current = new AbortController()
    lookupScroll.current = new AdaptiveLookupScroll(LOOKUP_LIMIT)
    setShouldFetchMore(true)

    const runFetch = () => {
      loadSearch({
        variables: {
          input: buildLookupRowsQueryInput({
            searchString: searchQuery ?? '',
            pagination: { limit: LOOKUP_LIMIT, offset: 0 },
          }),
        },
        context: {
          fetchOptions: {
            signal: pageAbortController.current.signal,
          },
        },
        onCompleted({ paginatedLookupRows }) {
          setHasFetchedOnce(true)
          setOptions(paginatedLookupRows.rows)
          const firstResult = paginatedLookupRows.rows.at(0)
          if (!firstResult) {
            setShouldFetchMore(false)
            return
          }
          // Only highlight the first result if no options are selected
          const latestLookupState = lookupStateRef.current
          if (
            !latestLookupState ||
            (Array.isArray(latestLookupState) && latestLookupState.length === 0)
          ) {
            setHighlightedOption(firstResult)
          }
          setShouldFetchMore(paginatedLookupRows.rows.length >= LOOKUP_LIMIT)
        },
      })
    }

    // First run fires immediately so the empty-state doesn't flash during the debounce window.
    if (!hasScheduledInitialFetch.current) {
      hasScheduledInitialFetch.current = true
      runFetch()
      return
    }

    const debounceTimeoutId = setTimeout(runFetch, 200)
    return () => {
      clearTimeout(debounceTimeoutId)
    }
  }, [searchQuery, loadSearch, lookupRefinementKey, lookupTable, buildLookupRowsQueryInput])

  /**
   * Adaptive lookup scroll to fetch more options dynamically
   */
  useEffect(() => {
    const ref = infiniteScroll.current

    const handleScroll = () => {
      if (!ref) {
        return
      }

      const scrollState = lookupScroll.current.updateScrollState(ref)

      // If the user has reached the trigger point, fetch more options if needed
      if (scrollState.reachedTriggerPoint && !loading && shouldFetchMore) {
        loadSearch({
          variables: {
            input: buildLookupRowsQueryInput({
              searchString: searchQuery ?? '',
              pagination: {
                limit: scrollState.nextBatchSize,
                offset: options.length,
              },
            }),
          },
        }).then((result) => {
          const newRows = result.data?.paginatedLookupRows.rows ?? []

          setOptions((prevOptions) => {
            const allRows = [...prevOptions, ...newRows]
            const uniqRows = uniqBy(allRows, (row) => row.id)

            setShouldFetchMore(newRows.length >= scrollState.nextBatchSize)

            return uniqRows
          })

          lookupScroll.current.handleFetchCompleted(newRows.length)
        })
      }
    }

    ref?.addEventListener('scroll', handleScroll)
    return () => ref?.removeEventListener('scroll', handleScroll)
  }, [
    buildLookupRowsQueryInput,
    searchQuery,
    loadSearch,
    lookupTable?.id,
    options.length,
    loading,
    shouldFetchMore,
  ])

  // Allow user to detach the lookup value if the column is not an array of lookups, the lookup
  // column is of type string or number, and, if on a form field, the form field's category
  // does not have any hidden fields that have a lookup column. Removed supported in table grid editing (adding, creating, editing lookups in a table).
  // Still supported in form editing.
  const showDetachButton = useMemo(() => {
    if (hideUnlinkLookupButton || !isFormContext) {
      return false
    }
    const isOverwritableColumnType = isOverwritableLookupColumnType(column.type)
    return (
      !isArray &&
      !isOverwritingLookup &&
      isOverwritableColumnType &&
      !categoryHasHiddenFieldsWithLookupColumn
    )
  }, [
    hideUnlinkLookupButton,
    isFormContext,
    column.type,
    isArray,
    isOverwritingLookup,
    categoryHasHiddenFieldsWithLookupColumn,
  ])

  // Show the button to cancel overwriting the lookup if currently overwriting. Removed supported in table grid editing (adding, creating, editing lookups in a table).
  // Still supported in form editing.
  const showCancelButton = useMemo(() => {
    if (hideUnlinkLookupButton || !isFormContext) {
      return false
    }
    return isOverwritingLookup
  }, [hideUnlinkLookupButton, isFormContext, isOverwritingLookup])

  const placeholderText = useMemo(() => {
    const nameDisplay = lookupTable ? lookupTable.name : column.name
    return !isOverwritingLookup && Array.isArray(lookupState) && lookupState.length > 0
      ? ''
      : t('record.lookup.search_placeholder', { nameDisplay })
  }, [isOverwritingLookup, lookupState, column.name, lookupTable, t])

  const isNumericLookup = useMemo(() => {
    return column.type === DataType.NUMBER
  }, [column.type])

  const selectedCells = useMemo(() => {
    if (!Array.isArray(lookupState)) {
      return []
    }
    return compact(
      lookupState.map((row) => {
        const forColumn = row.cells.find((cell) => cell.column.id === column.id)
        if (forColumn) {
          return forColumn
        }
        // Partial rows from cache (e.g. bulk modal) may omit the primary column; use any cell for chip labels.
        return row.cells[0]
      }),
    )
  }, [lookupState, column.id])

  const cellDisplays = useMemo<{ [key: string]: string }>(() => {
    if (!Array.isArray(lookupState)) {
      return {}
    }
    return selectedCells.reduce<Record<string, string>>((acc, cell) => {
      let display: string | null | undefined =
        getCellDisplay(cell, cell.column.id) ?? cell.userVisibleValue ?? undefined

      if (!display) {
        const row = lookupState.find((r) => r.cells.some((c) => c.id === cell.id))
        display = row?.name ?? ''
      }
      return { ...acc, [cell.id]: display }
    }, {})
  }, [getCellDisplay, lookupState, selectedCells])

  /**
   * Initialize the optionRefs with the options list
   */
  useEffect(() => {
    options.forEach((option) => {
      if (!(option.id in optionRefs.current)) {
        optionRefs.current[option.id] = createRef<HTMLDivElement>()
      }
    })
  }, [options])

  /**
   * Scroll to the highlighted option
   */
  useEffect(() => {
    if (!highlightedOption) {
      autocompleteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      return
    }
    const optionRef = get(optionRefs.current, highlightedOption.id, null)

    if (!optionRef || !optionRef.current) {
      return
    }

    optionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [highlightedOption])

  /**
   * Hydrate selected rows from props only (no list fetch — unified effect owns loadSearch).
   */
  useEffect(() => {
    if (!lookupTable) {
      return
    }

    const fetchLookupRows = async (rowIds: string[]) => {
      const result = await getSelectedLookupRows({ variables: { rowIds } })
      return result.data?.tableRows || []
    }

    const updateState = async () => {
      if (Array.isArray(values)) {
        const selectedLookupRowIds = values.map((value) => value.row.id)

        if (selectedLookupRowIds.length === 0) {
          setLookupState([])
          setLastSelectedOptions([])
        } else {
          const lookupRows = await fetchLookupRows(selectedLookupRowIds)
          setLookupState(lookupRows)
          setLastSelectedOptions(lookupRows)
          if (lookupRows.length > 0) {
            setHighlightedOption(lookupRows[0])
          }
        }
      } else if (values) {
        setLookupState(values)
        if (lookupColumnGroupLookupRowIds && lookupColumnGroupLookupRowIds.length > 0) {
          const lookupRows = await fetchLookupRows(lookupColumnGroupLookupRowIds)
          setLastSelectedOptions(lookupRows)
        }
      }
    }

    if (isInitialRender.current || !lastSelectedOptions) {
      updateState()
      isInitialRender.current = false
    }
  }, [
    lookupTable,
    values,
    lookupColumnGroupLookupRowIds,
    getSelectedLookupRows,
    lastSelectedOptions,
    setLookupState,
    loadSearch,
    parentRowId,
    lookupFilterExpressionId,
    userEnteredValues,
  ])

  // When backspace is hit, delete the last option in the selected options list
  const handleDeleteLast = () => {
    if (searchQuery != null && searchQuery.length > 0) {
      return
    }
    if (!values) {
      setLookupState(null)
      setLastSelectedOptions(null)
      return
    }
    if (Array.isArray(lookupState)) {
      const newSelectedOptions = [...lookupState]
      newSelectedOptions.pop()
      // Remove the last selected option in the array
      setLookupState(newSelectedOptions)
      setLastSelectedOptions(newSelectedOptions)
    }
  }

  // When the down arrow is hit, highlight the next option in the list
  const handleDownArrow = () => {
    if (!highlightedOption) {
      setHighlightedOption(first(options) ?? null)
      return
    }

    const currentIndex = options.findIndex((option) => option.id === highlightedOption.id)
    if (currentIndex === options.length - 1) {
      return
    }
    const newHighlightedOption = nth(options, currentIndex + 1) ?? null
    setHighlightedOption(newHighlightedOption)
  }

  // When the up arrow is hit, highlight the previous option in the list
  const handleUpArrow = () => {
    if (!highlightedOption) {
      return
    }

    const currentIndex = options.findIndex((option) => option.id === highlightedOption.id)
    if (currentIndex === 0) {
      setHighlightedOption(null)
      return
    }

    const newHighlightedOption = nth(options, currentIndex - 1) ?? null
    setHighlightedOption(newHighlightedOption)
  }

  // When enter is hit, select the highlighted option
  const handleEnter = () => {
    if (!highlightedOption) {
      return
    }
    handleSelectOption(highlightedOption)
  }

  /**
   * Handle keyboard shortcuts for the options list
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === localKeyMap.BACKSPACE) {
      handleDeleteLast()
    } else if (event.key === localKeyMap.DOWN_ARROW) {
      handleDownArrow()
    } else if (event.key === localKeyMap.UP_ARROW) {
      handleUpArrow()
    } else if (event.key === localKeyMap.ENTER) {
      if ((event.ctrlKey || event.metaKey) && onSave) {
        onSave()
      } else {
        handleEnter()
        event.preventDefault()
      }
      event.stopPropagation()
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    setSearchQuery('')
  }

  const handleNavigate = (event: MouseEvent<HTMLElement>) => {
    if (!lookupTable || isUnauth) {
      return
    }

    // If creating a child lookup on parent, route the user to the record
    if (parentRowId && isLookupOnChildTable) {
      event.stopPropagation()
      const path = getWebPath({
        objectType: BreadcrumbObjectType.TABLE_ROW,
        objectId: parentRowId,
      })
      window.open(path, '_blank')
    }

    event.stopPropagation()
    const path = getWebPath({
      objectType: BreadcrumbObjectType.TABLE,
      objectId: lookupTable.id,
    })
    window.open(path, '_blank')
  }

  const handleSelectOption = (option: LookupTableRow) => {
    if (Array.isArray(lookupState)) {
      if (isArray) {
        if (lookupState.some((selectedOption) => selectedOption.id === option.id)) {
          const newSelectedOptions = lookupState.filter(
            (selectedOption) => selectedOption.id !== option.id,
          )
          setLookupState(newSelectedOptions)
          setLastSelectedOptions(newSelectedOptions)
        } else {
          setLookupState([...lookupState, option])
          setLastSelectedOptions([...lookupState, option])
        }
      } else {
        // When clicked, if it is not selected, set that option. Else clear that option.
        if (lookupState.length === 1 && lookupState[0].id === option.id) {
          setLookupState(null)
          setLastSelectedOptions(null)
        } else {
          setLookupState([option])
          setLastSelectedOptions([option])
        }
        // Clear the search query in single-select only. This prevents the search query from being cleared in multi-select.
        setSearchQuery('')
        setHighlightedOption(null)
      }
    }
  }

  if (loadingLookupGroup) {
    return <LookupRowOptionsListSkeleton />
  }

  if (!lookupTable) {
    return <NoAccess objectType={NoAccessObjectType.TABLE} />
  }

  // If there is a selected option, show the preview
  let previewOption: LookupTableRow | null = null
  if (highlightedOption) {
    previewOption = highlightedOption
  } else if (Array.isArray(lookupState) && lookupState.length > 0) {
    previewOption = first(lookupState) ?? null
  }

  // Only string and number type lookup columns can be overwritten
  const getTableCellDisplay = (cellData: TableCellData): string | number | null => {
    switch (cellData.type) {
      case DossDataFormat.STRING:
      case DossDataFormat.NUMBER:
        return cellData.data
      case DossDataFormat.ENUM:
      case DossDataFormat.DATE:
      case DossDataFormat.DATE_TIME:
      case DossDataFormat.ARRAY:
      case DossDataFormat.BOOLEAN:
      case DossDataFormat.FILE:
      case DossDataFormat.ID:
      case DossDataFormat.FORMULA:
      case DossDataFormat.FORMULA_NUMBER:
      case DossDataFormat.FORMULA_STRING:
      case DossDataFormat.FORMULA_DATE:
      case DossDataFormat.FORMULA_DATE_TIME:
      case DossDataFormat.LOOKUP:
      case DossDataFormat.USER:
        return null
    }
  }

  const handleDetachClick = () => {
    if (!isFormContext) {
      return
    }
    if (Array.isArray(lookupState)) {
      const selectedOption = first(lookupState)
      const cell = selectedOption?.cells.find((cell) => {
        return cell.column.id === column.id
      })
      const cellData = cell?.data
      let cellDisplay: string | number | undefined
      if (cellData && isTableCellData(cellData)) {
        cellDisplay = getTableCellDisplay(cellData) ?? undefined
      }

      // Take the value of the original lookup cell and set that as the data
      setIsOverwritingLookup(true)

      setInputValue(cellDisplay?.toString() ?? '')
      setLookupState({ type: DossDataFormat.LOOKUP, data: cellDisplay, isOverwritten: true })
    }
  }

  const handleCancelDetachClick = () => {
    setIsOverwritingLookup(false)
    setLookupState(lastSelectedOptions ?? [])
  }

  // Handles when a user changes the value of the unlinked lookup
  const onChange = (newValue: string) => {
    if (!isFormContext) {
      return
    }
    setInputValue(newValue)
    if (isNumericLookup) {
      if (newValue === '' || newValue === '-' || newValue === '.') {
        setLookupState({ type: DossDataFormat.LOOKUP, isOverwritten: true })
      } else {
        const parsedValue = Number(newValue)
        if (!isNaN(parsedValue)) {
          setLookupState({ type: DossDataFormat.LOOKUP, data: parsedValue, isOverwritten: true })
        } else {
          setLookupState({ type: DossDataFormat.LOOKUP, data: newValue, isOverwritten: true })
        }
      }
    } else {
      setLookupState({ type: DossDataFormat.LOOKUP, data: newValue, isOverwritten: true })
    }
  }

  // If all options are selected, the all checked state is true.  If at least one cell is selected,
  // the checked state is indeterminate.  Otherwise the checked state is false because no cells are
  // selected
  const allCheckedState: CheckedState = (() => {
    if (options.length === 0) {
      return false
    }
    if (!Array.isArray(lookupState) || lookupState.length === 0) {
      return false
    }
    const selectedIdSet = new Set(lookupState.map((row) => row.id))
    let visibleSelectedCount = 0
    for (const opt of options) {
      if (selectedIdSet.has(opt.id)) {
        visibleSelectedCount++
      }
    }
    if (visibleSelectedCount === 0) {
      return false
    }
    if (visibleSelectedCount === options.length) {
      return true
    }
    return 'indeterminate'
  })()

  const handleToggleSelectAll = (checked: CheckedState) => {
    if (checked === false) {
      if (selectAllAddsToSelection) {
        handleRemoveVisibleFromSelection()
      } else {
        handleDeselectAllClick()
      }
    } else {
      handleSelectAllClick()
    }
  }

  const handleSelectAllClick = () => {
    if (selectAllAddsToSelection && Array.isArray(lookupState)) {
      const merged = uniqBy([...lookupState, ...options], (row) => row.id)
      setLookupState(merged)
      setLastSelectedOptions(merged)
    } else {
      setLookupState(options)
      setLastSelectedOptions(options)
    }
    setSearchQuery('')
    setHighlightedOption(null)
  }

  /** In bulk "add all" mode, uncheck removes only rows currently shown in the list (filter/sort/search/page), not the full selection. */
  const handleRemoveVisibleFromSelection = () => {
    if (!Array.isArray(lookupState)) {
      setLookupState([])
      setLastSelectedOptions([])
    } else {
      const visibleIds = new Set(options.map((row) => row.id))
      const next = lookupState.filter((row) => !visibleIds.has(row.id))
      setLookupState(next)
      setLastSelectedOptions(next)
    }
    setSearchQuery('')
    setHighlightedOption(null)
  }

  const handleDeselectAllClick = () => {
    setLookupState([])
    setLastSelectedOptions([])
    setSearchQuery('')
    setHighlightedOption(null)
  }

  const inTableDetails = origin === RecordCellEditOrigin.TABLE_DETAILS

  return (
    <div
      className={cn(
        'border-gray-4 my-auto flex min-w-[350px] flex-col gap-2 overflow-x-auto border-b px-4 pt-3 pb-0',
        isMobile && 'h-full overflow-hidden',
        // In a flexing container (resizable popover) fill the height rather than vertically center.
        fillHeight && 'my-0 min-h-0',
        className,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Popover header bar with table name and close button - shows on mobile in table details,
      or on desktop in all contexts except when in table details with an unlinked lookup */}
      {!hideLookupDialogHeader &&
        ((isMobile && inTableDetails) || (!isMobile && (!inTableDetails || !isUnlinkedLookup))) && (
          <LookupDialogHeader
            handleClose={handleClose}
            handleNavigate={handleNavigate}
            loading={loading}
            lookupTable={lookupTable}
          />
        )}
      {/* If there is a lookup filter expression, show it (from props or context e.g. form v1
      modal) — but only to admins and internal Doss users */}
      {canViewLookupFilterExpression && lookupFilterExpressionId && (
        <div className="flex w-full min-w-0 items-center gap-1 p-2 pt-0">
          <div className="flex shrink-0 items-center gap-1">
            <FaFilter />
          </div>
          <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
            <div className="inline-block min-w-min">
              <ConditionExpression
                fieldContextNameMap={lookupFilterFieldContextNameMap ?? undefined}
                fieldContextTypeMap={lookupFilterFieldContextTypeMap ?? undefined}
                fieldNameMap={lookupFilterFieldNameMap ?? undefined}
                logicExpressionId={lookupFilterExpressionId}
                logicExpressionString={lookupFilterExpressionString}
              />
            </div>
          </div>
        </div>
      )}
      <div className={'flex flex-col gap-2'}>
        <div className="relative flex w-full flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-0">
          {!isOverwritingLookup ? (
            <Autocomplete
              ref={autocompleteRef}
              autoFocus={autoFocus}
              className="grow"
              disableClearable
              freeSolo
              inputValue={searchQuery ?? ''}
              multiple
              onInputChange={(event, value, message) => {
                if (message === 'input') {
                  setSearchQuery(value)
                } else if (message === 'clear') {
                  setLookupState(null)
                  setLastSelectedOptions(null)
                }
              }}
              open={false}
              options={[]}
              renderInput={(params) => (
                <TextField
                  autoFocus
                  className={classes.search}
                  error={!!error}
                  placeholder={placeholderText}
                  startAdornment={
                    <div className="max-h-[130px] overflow-y-auto">
                      {params.InputProps.startAdornment}
                    </div>
                  }
                  variant="rounded"
                  {...params}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <LookupCellChipV2
                    allowNavigate={false}
                    lookup={{
                      id: option.id,
                      value: get(cellDisplays, option.id, ''),
                      rowId: option.row.id,
                      column,
                      documents: option.documents,
                    }}
                    type="chip"
                    {...getTagProps({ index })}
                    key={index}
                    onDelete={() => {
                      if (!lookupState || !Array.isArray(lookupState)) {
                        return
                      }

                      const newSelectedOptions = lookupState.filter(
                        (selectedOption) => selectedOption.id !== option.row.id,
                      )
                      setLookupState(newSelectedOptions)
                      setLastSelectedOptions(newSelectedOptions)
                    }}
                  />
                ))
              }
              value={selectedCells}
            />
          ) : (
            <TextField
              className={cn(classes.overwriteField, 'flex flex-grow')}
              error={!!error}
              multiline
              onChange={(event) => {
                onChange(event.target.value)
              }}
              type="text"
              value={inputValue}
              variant="rounded"
            />
          )}
          {(showDetachButton || showCancelButton) && (
            <OverwriteLookupButton
              action={showDetachButton ? 'detach' : 'attach'}
              className={cn('rounded md:rounded-l-none', isMobile && 'h-8')}
              handleCancelClick={handleCancelDetachClick}
              handleDetachClick={handleDetachClick}
              isUnlinkedLookup={isUnlinkedLookup}
            />
          )}
        </div>
      </div>
      {error && !hideInnerError && (
        <Typography
          className={cn(
            'text-red-9',
            inTableDetails && isUnlinkedLookup && isOverwritingLookup && 'pb-4',
          )}
          variant="caption"
        >
          {error}
        </Typography>
      )}
      {/* If used in a form array category footer, display the checkbox to select/deselect all options */}
      {showSelectAllButton && options.length > 0 && (
        <div className="px-4">
          <Checkbox
            checked={allCheckedState}
            label={selectAllAddsToSelection ? t('lookup.add_all') : t('lookup.select_all')}
            onCheckedChange={(checked) => handleToggleSelectAll(checked)}
          />
        </div>
      )}
      {/* Show the list of all options */}
      {!isOverwritingLookup ? (
        <div
          className={cn(
            'relative flex flex-col md:flex-row md:h-[300px] md:max-h-[300px]',
            isMobile && 'overflow-hidden h-full',
            // Grow to fill the resizable container instead of the fixed desktop height.
            fillHeight && 'md:h-auto md:max-h-none md:min-h-0 md:flex-1',
          )}
        >
          <div
            ref={infiniteScroll}
            className={cn(
              'border-gray-4 flex md:flex-1 flex-col overflow-x-hidden overflow-y-scroll p-2 pt-1',
              'md:w-1/2 md:border-e', // Add border only on desktop
              'border-b md:border-b-0', // Add bottom border on mobile
              isMobile && 'h-1/2',
            )}
          >
            {options.map((option) => (
              <div
                key={option.id}
                ref={optionRefs.current[option.id]}
                onMouseEnter={() => setHighlightedOption(option)}
              >
                <LookupMenuItem
                  columnId={column.id}
                  handleClick={() => handleSelectOption(option)}
                  isHighlighted={highlightedOption?.id === option.id}
                  option={option}
                  searchQuery={searchQuery ?? ''}
                  selected={
                    Array.isArray(lookupState)
                      ? lookupState.some((selectedOption) => selectedOption.id === option.id)
                      : false
                  }
                />
              </div>
            ))}
            {options.length === 0 && (loading || !hasFetchedOnce) && (
              <div className="flex flex-1 items-center justify-center p-6 text-gray-8">
                <Loader2 className="animate-spin" size={20} />
              </div>
            )}
            {options.length === 0 && !loading && hasFetchedOnce && (
              <If condition={isUnauth}>
                <Then>
                  <div className="flex flex-wrap items-center justify-start gap-1 p-3">
                    <Typography variant="body1">{t('lookup.no_permission')}</Typography>
                  </div>
                </Then>
                <Else>
                  <div className="flex flex-wrap items-center justify-start gap-1 p-3">
                    <Typography variant="body1">
                      {t('lookup.add_new_record')}
                      <Link
                        href={getWebPath({
                          objectType: BreadcrumbObjectType.TABLE,
                          objectId: lookupTable.id,
                        })}
                        target="_blank"
                      >
                        {lookupTable.name}
                      </Link>
                    </Typography>
                    <Typography variant="body1">{t('lookup.refresh_options')}</Typography>
                  </div>
                </Else>
              </If>
            )}
          </div>
          <div className="flex-1 overflow-x-hidden overflow-y-scroll p-2 pt-1 md:w-1/2">
            {previewOption ? (
              <LookupMenuItemPreview
                columnId={column.id}
                option={previewOption}
                parentRowId={isLookupOnChildTable ? parentRowId : undefined}
                searchQuery={searchQuery ?? ''}
              />
            ) : (
              <Typography
                className="flex items-center text-wrap px-6 py-3 text-center text-gray-10"
                variant="body2"
              >
                {t('record.lookup.search_for_option')}
              </Typography>
            )}
          </div>
        </div>
      ) : (
        !inTableDetails && (
          <div
            className={cn(
              'flex flex-col md:h-[300px] md:max-h-[300px] md:flex-row',
              isMobile && 'block',
            )}
          >
            <OverwriteLookupBanner
              error={!!error}
              header={
                error
                  ? t('record.lookup.to_overwrite_lookup')
                  : t('record.lookup.you_are_adding_text')
              }
              lookupTable={lookupTable}
            />
          </div>
        )
      )}
    </div>
  )
}

const useStyles = tss.create(() => {
  return {
    search: {
      [`& .${outlinedInputClasses.root}`]: {
        paddingInlineStart: '8px',
        paddingTop: '4px',
        paddingInlineEnd: '30px',
        paddingBottom: '4px',
        gap: '4px',
        flexWrap: 'wrap',
      },
      [`& .${inputClasses.multiline}`]: {
        [`& .${inputBaseClasses.inputMultiline}`]: {
          width: 'auto',
        },
      },
    },
    overwriteField: {
      [`& .${outlinedInputClasses.root}`]: {
        paddingInlineStart: '8px',
        paddingTop: '4px',
        paddingInlineEnd: '30px',
        paddingBottom: '4px',
        gap: '4px',
        height: '34px',
      },
    },
  }
})
